'use client';

import Image from 'next/image';
import { useEffect, useState } from 'react';
import type { BoardCategory } from '@/types/board';

/**
 * PageBanner 컴포넌트 Props 인터페이스
 * 
 * 페이지 배너에 표시할 카테고리 정보를 정의합니다.
 */
interface PageBannerProps {
  category: BoardCategory;
}

/**
 * 페이지 배너 컴포넌트
 * 
 * 각 페이지 상단에 표시되는 배너 이미지와 제목을 포함하는 컴포넌트입니다.
 * 이미지 로딩 시 부드러운 애니메이션 효과를 제공하며, 반응형 디자인을 지원합니다.
 * 
 * 주요 기능:
 * - 배경 이미지 표시 (banner.jpg)
 * - 카테고리별 제목 표시
 * - 이미지 로딩 애니메이션 (스케일 효과)
 * - 텍스트 페이드인 애니메이션
 * - 그라데이션 오버레이
 * - 반응형 높이 및 타이포그래피
 */
export function PageBanner({ category }: PageBannerProps) {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  // 컴포넌트 마운트 상태 관리
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // 서버와 클라이언트 초기 상태를 일치시키기 위해 isMounted 체크
  const shouldShowAnimation = isMounted && isImageLoaded;

  /**
   * 이미지 로딩 완료 핸들러
   * 
   * 이미지가 완전히 로드되면 애니메이션을 시작할 수 있도록 상태를 업데이트합니다.
   */
  const handleImageLoad = () => {
    setIsImageLoaded(true);
  };

  return (
    <section className="relative pt-10 sm:pt-12 h-[320px] sm:h-[420px] w-full overflow-hidden">
      {/* 상단 그라데이션 오버레이 */}
      <div className="absolute top-0 left-0 right-0 h-32 sm:h-50 bg-gradient-to-b from-black to-transparent z-10" />

      {/* 이미지 로딩 중일 때 검정화면 표시 */}
      {!isImageLoaded && (
        <div className="absolute inset-0 bg-black z-0" />
      )}

      {/* 배경 이미지 */}
      <Image
        src="/banner/banner.jpg"
        alt={`${category.label} 배너`}
        fill
        className={`object-cover transition-transform duration-1000 ease-out ${shouldShowAnimation ? 'scale-100' : 'scale-125'
          }`}
        priority
        onLoad={handleImageLoad}
      />

      {/* 추가 오버레이 레이어 */}
      <div className="absolute inset-0" />

      {/* 중앙 콘텐츠 영역 */}
      <div className="relative flex h-full items-center justify-center z-10">
        {/* CBNU 서브타이틀 */}
        <h3
          className={`absolute top-24 sm:top-31 left-1/2 -translate-x-1/2 text-lg sm:text-2xl font-bold text-white/70 transition-all duration-700 ease-out ${shouldShowAnimation
            ? 'translate-y-0 opacity-100'
            : 'translate-y-8 opacity-0'
            }`}
        >
          CBNU
        </h3>

        {/* 메인 제목 */}
        <h1
          className={`text-3xl sm:text-5xl font-bold text-white transition-all duration-700 ease-out delay-200 ${shouldShowAnimation
            ? 'translate-y-0 opacity-100'
            : 'translate-y-8 opacity-0'
            }`}
        >
          {category.mainMenu || category.label}
        </h1>
      </div>
    </section>
  );
} 